// client/src/coms/views/VoxemeraHomeView.js
// The Voxemera welcome screen at "/" on voxemera.com hosts. Signed-in visitors are sent straight on to the app
// (their last server/channel) unless they chose to come here (the Voxemera button in the server rail sets a
// one-shot flag) or an invite / public-server join is waiting. Everyone else sees the welcome dashboard:
// primary actions, the public server list (servers their owners listed, /api/coms/discover/servers), quick
// access with genuinely recent destinations, and platform-aware app installation.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginButton from '../components/LoginButton';
import ComsModal from '../components/ComsModal';
import { getPersistedLastLocation } from '../components/comslayout_parts/comsSession';
import { comsApiGet } from '../comsApi';
import EncryptionConsentHost, { joinByInvite, joinWithConsent } from '../e2ee/EncryptionConsentHost';
import { paintCachedTheme } from '../personalisation/PersonalisationProvider';
import { DESKTOP_MANIFEST_URL, DOWNLOADS_URL, NUDGE_KEY, appPromotion, buildAvailability, dismissNudge, markActed, readPlatformSignals, recordVisit, shouldShowNudge } from './welcome/welcomePlatform';
import { AREA_LABELS, buildRecent, comsPathForArea, initialsOf, pickHomeServer, readPendingPublicJoin, readRecentInputs, serverKeyOf, writePendingPublicJoin } from './welcome/welcomeData';
import { ActionButton, AppGet, AppNudge, CommunitiesMenu, ContinuePanel, Icon, PublicServers } from './welcome/WelcomeSections';
import '../coms.css';
import './welcome/welcome.css';

const LS_LAST_SERVER_ID = 'lastCommsServerId';
const MANUAL_LANDING_SESSION_KEY = 'voxemera_manual_landing_once';

function readLastServerId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return String(window.localStorage.getItem(LS_LAST_SERVER_ID) || '').trim();
  } catch {
    return '';
  }
}

function buildComsLaunchPath() {
  const last = getPersistedLastLocation();
  const savedServerId = String((last && last.serverId) || readLastServerId() || '').trim();
  if (savedServerId) return `/comms/${encodeURIComponent(savedServerId)}`;
  return '/comms';
}

function consumeManualLandingRequest() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    const v = window.sessionStorage.getItem(MANUAL_LANDING_SESSION_KEY);
    window.sessionStorage.removeItem(MANUAL_LANDING_SESSION_KEY);
    return v === '1';
  } catch {
    return false;
  }
}

function pickDisplayName(user) {
  if (!user) return '';
  const v = user.display_name || user.displayName || user.username || user.name || user.email || '';
  return String(v || '').trim();
}

async function fetchMe() {
  try {
    const resA = await fetch('/api/me', { credentials: 'include', cache: 'no-store' });
    if (resA.ok) {
      const a = await resA.json().catch(() => null);
      if (a && (a.id != null || a.user_id != null)) return a;
    }
  } catch {}

  try {
    const resB = await fetch('/api/user/me', { credentials: 'include', cache: 'no-store' });
    if (resB.ok) {
      const b = await resB.json().catch(() => null);
      if (b && (b.id != null || b.user_id != null)) return b;
    }
  } catch {}

  return null;
}

function headSnapshot() {
  if (typeof document === 'undefined') return { links: [], theme: null, title: '' };
  const links = Array.from(document.head.querySelectorAll('link[rel]')).map((el) => ({
    rel: (el.getAttribute('rel') || '').trim(),
    href: el.getAttribute('href'),
    sizes: el.getAttribute('sizes'),
    type: el.getAttribute('type'),
  }));
  const m = document.head.querySelector('meta[name="theme-color"]');
  const theme = m ? m.getAttribute('content') : null;
  const title = document.title || '';
  return { links, theme, title };
}

function removeRel(rel) {
  const nodes = Array.from(document.head.querySelectorAll(`link[rel="${rel}"]`));
  nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
}

function addLink(rel, href, sizes, type) {
  const el = document.createElement('link');
  el.setAttribute('rel', rel);
  if (href != null) el.setAttribute('href', href);
  if (sizes) el.setAttribute('sizes', sizes);
  if (type) el.setAttribute('type', type);
  document.head.appendChild(el);
}

function setThemeColor(color) {
  let m = document.head.querySelector('meta[name="theme-color"]');
  if (!m) {
    m = document.createElement('meta');
    m.setAttribute('name', 'theme-color');
    document.head.appendChild(m);
  }
  m.setAttribute('content', color);
}

function useVoxHeadBranding() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const prev = headSnapshot();

    document.title = 'Voxemera';

    removeRel('icon');
    removeRel('shortcut icon');
    removeRel('apple-touch-icon');
    removeRel('manifest');

    addLink('icon', '/vox-favicon.ico');
    addLink('icon', '/vox-favicon-192x192.png', '192x192', 'image/png');
    addLink('icon', '/vox-favicon-512x512.png', '512x512', 'image/png');
    addLink('icon', '/vox-favicon-32x32.png', '32x32', 'image/png');
    addLink('icon', '/vox-favicon-16x16.png', '16x16', 'image/png');
    addLink('apple-touch-icon', '/vox-apple-touch-icon.png');
    addLink('manifest', '/vox-site.webmanifest');

    setThemeColor('#050712');

    return () => {
      document.title = prev.title || '';

      removeRel('icon');
      removeRel('shortcut icon');
      removeRel('apple-touch-icon');
      removeRel('manifest');

      prev.links.forEach((l) => {
        const rel = (l.rel || '').trim();
        const touched = rel === 'icon' || rel === 'shortcut icon' || rel === 'apple-touch-icon' || rel === 'manifest';
        if (!touched) return;
        if (!l.href) return;
        addLink(rel, l.href, l.sizes, l.type);
      });

      if (prev.theme != null) setThemeColor(prev.theme);
    };
  }, []);
}

function pickServerIdFromPayload(p) {
  const obj = p && typeof p === 'object' ? p : {};

  const direct =
    obj.server_key != null
      ? String(obj.server_key)
      : obj.public_id != null
        ? String(obj.public_id)
        : obj.server_id != null
          ? String(obj.server_id)
          : obj.serverId != null
            ? String(obj.serverId)
            : obj.id != null
              ? String(obj.id)
              : obj.server && obj.server.server_key != null
                ? String(obj.server.server_key)
                : obj.server && obj.server.public_id != null
                  ? String(obj.server.public_id)
                  : obj.server && obj.server.id != null
                    ? String(obj.server.id)
                    : obj.server && obj.server.server_id != null
                      ? String(obj.server.server_id)
                      : '';

  const out = String(direct || '').trim();
  if (out) return out;

  const list = Array.isArray(obj.servers) ? obj.servers : [];
  if (list.length) {
    const first = list[0] || {};
    const id =
      first.server_key != null
        ? String(first.server_key)
        : first.public_id != null
          ? String(first.public_id)
          : first.id != null
            ? String(first.id)
            : first.server_id != null
              ? String(first.server_id)
              : first.serverId != null
                ? String(first.serverId)
                : '';
    const out2 = String(id || '').trim();
    return out2 ? out2 : null;
  }

  return null;
}

function getInviteFromSearch(search) {
  try {
    const s = String(search || '');
    if (!s) return '';
    const sp = new URLSearchParams(s.startsWith('?') ? s : `?${s}`);
    const v = sp.get('invite');
    return String(v || '').trim();
  } catch {
    return '';
  }
}

function readPendingInvite() {
  try {
    if (typeof window === 'undefined') return '';
    const v = window.sessionStorage.getItem('vox_pending_invite');
    return String(v || '').trim();
  } catch {
    return '';
  }
}

function writePendingInvite(code) {
  try {
    if (typeof window === 'undefined') return;
    const v = String(code || '').trim();
    if (!v) return;
    window.sessionStorage.setItem('vox_pending_invite', v);
  } catch {}
}

function clearPendingInvite() {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('vox_pending_invite');
  } catch {}
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || (data && data.error)) {
    const msg = (data && data.error) || `Request failed (${res.status})`;
    throw new Error(String(msg || 'Request failed'));
  }

  return data || {};
}


// Reduced motion: the system setting, or the account's own Voxemera setting (Settings → Appearance), which is
// cached on this device by the personalisation provider.
function readAccountReduceMotion(userId) {
  try {
    if (!userId || typeof window === 'undefined') return false;
    const raw = window.localStorage.getItem(`vx:prefs:u:${userId}`);
    const cached = raw ? JSON.parse(raw) : null;
    const v = cached && cached.prefs && cached.prefs.theme ? cached.prefs.theme.reduceMotion : '';
    return v === 'on';
  } catch {
    return false;
  }
}

function useReducedMotion(userId) {
  const [system, setSystem] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setSystem(!!mq.matches);
    onChange();
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return system || readAccountReduceMotion(userId);
}

// Accepts SERVERKEY:CODE as well as pasted invite links (…/invite/CODE or …?invite=CODE).
function normaliseInviteInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  try {
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      const q = u.searchParams.get('invite');
      if (q) return q.trim();
      const m = u.pathname.match(/\/invite\/([^/?#]+)/i);
      if (m) return decodeURIComponent(m[1]).trim();
    }
  } catch {}
  return s;
}

const ACTION_CARDS = [
  { id: 'canvas', title: 'Open Canvas', sub: 'Draw and collaborate', icon: Icon.pencil(32) },
  { id: 'studio', title: 'Open Studio', sub: 'Make and share audio', icon: Icon.sliders(32) },
  { id: 'browse', title: 'Browse Servers', sub: 'Find communities', icon: Icon.people(34) },
  { id: 'invite', title: 'Enter Invite Code', sub: 'Join a server', icon: Icon.link(32) },
];

const RECENT_CLEARED_KEY = 'vxw:recentClearedAt';
function readRecentClearedAt() {
  try {
    return Number(window.localStorage.getItem(RECENT_CLEARED_KEY)) || 0;
  } catch {
    return 0;
  }
}

function readNudgeState() {
  try {
    return JSON.parse(window.localStorage.getItem(NUDGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function writeNudgeState(state) {
  try {
    window.localStorage.setItem(NUDGE_KEY, JSON.stringify(state));
  } catch {}
}

// The JSON at url, or null (missing files come back as the SPA's HTML page, which is not JSON).
async function fetchJsonOrNull(url) {
  try {
    const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return await res.json().catch(() => null);
  } catch {
    return null;
  }
}

export default function VoxemeraHomeView({ user, setUser, onLogout }) {
  useVoxHeadBranding();

  const navigate = useNavigate();
  const location = useLocation();

  const initialSignedIn = !!(user && (user.id != null || user.user_id != null));
  const [meLoading, setMeLoading] = useState(!initialSignedIn);
  const [joinBusy, setJoinBusy] = useState(false);
  const [allowManualLanding] = useState(() => consumeManualLandingRequest());

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteModalCode, setInviteModalCode] = useState('');
  const [inviteModalPublicName, setInviteModalPublicName] = useState('');
  const [inviteModalErr, setInviteModalErr] = useState('');

  const [inviteLoginModalOpen, setInviteLoginModalOpen] = useState(false);
  const [inviteLoginModalCode, setInviteLoginModalCode] = useState('');

  const [inviteEntryOpen, setInviteEntryOpen] = useState(false);
  const [inviteEntryValue, setInviteEntryValue] = useState('');

  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [installBusy, setInstallBusy] = useState('');
  const [canInstall, setCanInstall] = useState(false);
  const [runtimeTick, setRuntimeTick] = useState(0);
  const [installHelp, setInstallHelp] = useState('');
  // Builds that really exist (null while loading), from the updater manifest and the optional downloads list.
  const [availability, setAvailability] = useState(null);
  const availabilityRef = useRef(null);
  const [nudgeVisible, setNudgeVisible] = useState(false);

  const [publicState, setPublicState] = useState({ status: 'loading', servers: [] });
  const [showAllServers, setShowAllServers] = useState(false);
  const [publicBusyKey, setPublicBusyKey] = useState('');
  const [publicNotes, setPublicNotes] = useState({});
  const [publicLoginServer, setPublicLoginServer] = useState(null);
  const [pendingPublicJoin, setPendingPublicJoin] = useState(() => readPendingPublicJoin());
  // Set once a join waiting from before sign-in runs, so its result (e.g. "awaiting approval") stays on screen.
  const [stayOnLanding, setStayOnLanding] = useState(false);

  const [myServers, setMyServers] = useState({ status: 'idle', servers: [] });
  const [logos, setLogos] = useState({});
  const [dmThreads, setDmThreads] = useState([]);
  const [actionNote, setActionNote] = useState('');
  const [communitiesOpen, setCommunitiesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [recentClearedAt, setRecentClearedAt] = useState(() => readRecentClearedAt());

  const autoInvitePromptedRef = useRef('');
  // An invite whose "Login required" prompt was cancelled is not prompted again on this visit.
  const inviteLoginDismissedRef = useRef('');
  const loginBtnWrapRef = useRef(null);
  const publicSectionRef = useRef(null);
  const pendingJoinRanRef = useRef('');

  const isSignedIn = !!(user && (user.id != null || user.user_id != null));
  const userId = isSignedIn ? String(user.id != null ? user.id : user.user_id) : '';
  const reduceMotion = useReducedMotion(userId);
  const hasLaunchBlockingInvite = !!(getInviteFromSearch(location && location.search ? location.search : '') || readPendingInvite());
  const shouldBootRedirect = isSignedIn && !hasLaunchBlockingInvite && !pendingPublicJoin && !stayOnLanding && !allowManualLanding;
  const shouldHoldLandingPaint = meLoading || shouldBootRedirect;
  const displayName = useMemo(() => pickDisplayName(user), [user]);

  // runtimeTick re-reads the platform after the app gets installed or the display mode changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const platform = useMemo(() => readPlatformSignals(), [runtimeTick]);
  const promo = useMemo(
    () => appPromotion({ os: platform.os, runtime: platform.runtime, availability, chromium: platform.chromium, safari: platform.safari, canPromptInstall: canInstall && !!deferredInstallPrompt }),
    [platform, availability, canInstall, deferredInstallPrompt]
  );

  const logoSrc = (process.env.PUBLIC_URL || '') + '/vox-favicon-256x256.png';

  useLayoutEffect(() => {
    // The theme the account last used on this device (light themes get the light welcome screen).
    paintCachedTheme();
  }, []);

  useLayoutEffect(() => {
    if (!shouldBootRedirect) return;
    navigate(buildComsLaunchPath(), { replace: true });
  }, [shouldBootRedirect, navigate]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!alive) return;
      if (isSignedIn) {
        setMeLoading(false);
        return;
      }
      if (typeof setUser !== 'function') {
        setMeLoading(false);
        return;
      }

      setMeLoading(true);
      try {
        const me = await fetchMe();
        if (!alive) return;
        if (me && (me.id != null || me.user_id != null)) setUser(me);
      } finally {
        if (alive) setMeLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isSignedIn, setUser]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    const hostname = String(window.location.hostname || '').trim().toLowerCase();
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    const isSecure = !!window.isSecureContext || isLocal;
    if (!isSecure) return;

    navigator.serviceWorker.register('/vox-sw.js', { scope: '/' }).catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
    const sync = () => setRuntimeTick((n) => n + 1);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
      setCanInstall(true);
    };

    const onAppInstalled = () => {
      setDeferredInstallPrompt(null);
      setCanInstall(false);
      setInstallBusy('');
      sync();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    if (mq) {
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', sync);
      else mq.addListener(sync);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);

      if (mq) {
        if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', sync);
        else mq.removeListener(sync);
      }
    };
  }, []);

  // Which builds exist, from the release infrastructure itself (same host, so beta gets beta builds). Only in a
  // browser: installed apps are never offered a download.
  const loadAvailability = useCallback(async () => {
    const [manifest, downloads] = await Promise.all([fetchJsonOrNull(DESKTOP_MANIFEST_URL), fetchJsonOrNull(DOWNLOADS_URL)]);
    const av = buildAvailability({ manifest, downloads, origin: window.location.origin });
    availabilityRef.current = av;
    setAvailability(av);
    return av;
  }, []);

  useEffect(() => {
    if (platform.runtime !== 'browser') return;
    loadAvailability();
  }, [platform.runtime, loadAvailability]);

  // The one optional suggestion: returning signed-out browser visitors, a few seconds after the page settles.
  const nudgeOption = promo.primary;
  useEffect(() => {
    if (shouldHoldLandingPaint || platform.runtime !== 'browser') return undefined;
    const now = Date.now();
    const state = recordVisit(readNudgeState(), now);
    writeNudgeState(state);
    let shownThisSession = false;
    try {
      shownThisSession = window.sessionStorage.getItem(NUDGE_KEY + '_shown') === '1';
    } catch {}
    if (shownThisSession || !shouldShowNudge(state, { now, signedIn: isSignedIn, hasPrimary: !!nudgeOption && availability !== null, runtime: platform.runtime })) return undefined;
    const t = window.setTimeout(() => {
      try {
        window.sessionStorage.setItem(NUDGE_KEY + '_shown', '1');
      } catch {}
      setNudgeVisible(true);
    }, 6000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHoldLandingPaint, platform.runtime, isSignedIn, !!nudgeOption, availability !== null]);

  useEffect(() => {
    if (isSignedIn && nudgeVisible) setNudgeVisible(false);
  }, [isSignedIn, nudgeVisible]);

  function dismissAppNudge() {
    writeNudgeState(dismissNudge(readNudgeState(), Date.now()));
    setNudgeVisible(false);
  }

  // ---------- public servers ----------
  const loadPublicServers = useCallback(async () => {
    setPublicState((s) => ({ status: s.servers.length ? s.status : 'loading', servers: s.servers }));
    try {
      const res = await fetch('/api/coms/discover/servers', { credentials: 'include', cache: 'no-store', headers: { Accept: 'application/json' } });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !Array.isArray(data.servers)) throw new Error('bad response');
      setPublicState({ status: 'ok', servers: data.servers });
    } catch {
      setPublicState((s) => (s.servers.length ? s : { status: 'error', servers: [] }));
    }
  }, []);

  useEffect(() => {
    if (shouldHoldLandingPaint) return;
    loadPublicServers();
  }, [shouldHoldLandingPaint, isSignedIn, loadPublicServers]);

  // Visibility changes (Server Settings → Overview) show up without a reload.
  useEffect(() => {
    if (shouldHoldLandingPaint) return undefined;
    const refresh = () => {
      try {
        if (document.visibilityState === 'hidden') return;
      } catch {}
      loadPublicServers();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('vox-discovery-changed', refresh);
    document.addEventListener('visibilitychange', refresh);
    const t = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('vox-discovery-changed', refresh);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(t);
    };
  }, [shouldHoldLandingPaint, loadPublicServers]);

  // ---------- the account's servers, logos and recent DMs ----------
  const loadMine = useCallback(async () => {
    setMyServers((s) => ({ status: s.status === 'ok' ? 'ok' : 'loading', servers: s.servers }));
    try {
      const data = await comsApiGet('/servers');
      const list = data && Array.isArray(data.servers) ? data.servers : [];
      setMyServers({ status: 'ok', servers: list });
      const keys = list.slice(0, 12).map((s) => serverKeyOf(s)).filter(Boolean);
      const pairs = await Promise.all(
        keys.map(async (k) => {
          try {
            const r = await comsApiGet(`/server-logo/${encodeURIComponent(k)}`);
            return [k, (r && r.logo_url) || ''];
          } catch {
            return [k, ''];
          }
        })
      );
      setLogos(Object.fromEntries(pairs.filter((p) => p[1])));
    } catch {
      setMyServers({ status: 'error', servers: [] });
    }
    try {
      const dm = await comsApiGet('/dms');
      setDmThreads(dm && Array.isArray(dm.threads) ? dm.threads : []);
    } catch {
      setDmThreads([]);
    }
  }, []);

  useEffect(() => {
    if (!isSignedIn || shouldHoldLandingPaint) return;
    loadMine();
  }, [isSignedIn, shouldHoldLandingPaint, loadMine]);

  const recent = useMemo(() => {
    if (!isSignedIn || myServers.status !== 'ok') return null;
    return buildRecent({ servers: myServers.servers, dmThreads, clearedAt: recentClearedAt, ...readRecentInputs() });
  }, [isSignedIn, myServers, dmThreads, recentClearedAt]);

  // "Clear" on Recently active: hides activity up to now on this device (nothing is deleted).
  const clearRecent = useCallback(() => {
    const now = Date.now();
    try {
      window.localStorage.setItem(RECENT_CLEARED_KEY, String(now));
    } catch {}
    setRecentClearedAt(now);
  }, []);

  const homeServerKey = useMemo(() => {
    if (myServers.status !== 'ok') return '';
    return serverKeyOf(pickHomeServer(myServers.servers, readRecentInputs()));
  }, [myServers]);

  async function changePublicName(newUsername) {
    const nextName = String(newUsername || '').trim();
    if (!nextName) return;

    await postJson('/api/user/change-username', { newUsername: nextName });

    if (!setUser) {
      const me = await fetchMe().catch(() => null);
      if (me && (me.id != null || me.user_id != null)) {
        setUser(me);
      } else if (user) {
        setUser({ ...user, username: nextName });
      }
    }
  }

  async function joinByInviteCode(invite_code) {
    const code = String(invite_code || '').trim();

    if (!code) return { ok: false, msg: 'Missing invite code.' };

    setJoinBusy(true);
    try {
      // Encrypted servers open the encryption consent screen (EncryptionConsentHost below) before joining.
      const data = await joinByInvite({ invite_code: code });
      const sid = pickServerIdFromPayload(data);
      return { ok: true, msg: '', serverId: sid || null };
    } catch (e) {
      if (e && e.status === 401) return { ok: false, msg: 'You need to be signed in to join a server.' };
      return { ok: false, msg: (e && e.message) || 'Failed to join server.' };
    } finally {
      setJoinBusy(false);
    }
  }

  function openInviteJoinModal(inviteCode, options = {}) {
    const code = String(inviteCode || '').trim();
    if (!code) return;
    setInviteModalErr('');
    setInviteModalCode(code);
    setInviteModalPublicName(String(options.defaultPublicName || pickDisplayName(user) || ''));
    setInviteModalOpen(true);
  }

  function closeInviteJoinModal() {
    if (joinBusy) return;
    setInviteModalOpen(false);
    setInviteModalErr('');
    setInviteModalPublicName('');
  }

  function openInviteLoginModal(inviteCode) {
    const code = String(inviteCode || '').trim();
    if (!code) return;
    setInviteLoginModalCode(code);
    setInviteLoginModalOpen(true);
  }

  function closeInviteLoginModal() {
    setInviteLoginModalOpen(false);
  }

  function cancelInviteLoginModal() {
    inviteLoginDismissedRef.current = String(inviteLoginModalCode || '').trim();
    clearPendingInvite();
    setInviteLoginModalOpen(false);
  }

  function triggerOAuthLogin() {
    try {
      const root = loginBtnWrapRef.current;
      if (!root) return false;
      const btn = root.querySelector('button, a');
      if (!btn) return false;
      btn.click();
      return true;
    } catch {
      return false;
    }
  }

  async function submitInviteJoinFromModal(changeNameFirst) {
    const code = String(inviteModalCode || '').trim();
    if (!code || joinBusy) return;

    const desiredPublicName = String(inviteModalPublicName || '').trim();
    const currentVisibleName = String(displayName || '').trim();

    setInviteModalErr('');

    try {
      if (changeNameFirst) {
        if (!desiredPublicName) {
          setInviteModalErr('Enter a public name first.');
          return;
        }

        if (desiredPublicName.length < 3 || desiredPublicName.length > 32) {
          setInviteModalErr('Public name must be between 3 and 32 characters.');
          return;
        }

        if (!currentVisibleName || desiredPublicName !== currentVisibleName) {
          await changePublicName(desiredPublicName);
        }
      }

      const r = await joinByInviteCode(code);
      if (!r.ok) {
        setInviteModalErr(r.msg || 'Failed to join server.');
        return;
      }

      clearPendingInvite();
      setInviteModalOpen(false);
      setInviteModalErr('');
      setInviteModalPublicName('');
      navigate(r.serverId ? `/comms/${encodeURIComponent(r.serverId)}` : '/comms');
    } catch (e) {
      setInviteModalErr(String((e && e.message) || e || 'Failed to continue'));
    }
  }

  useEffect(() => {
    const inviteFromUrl = getInviteFromSearch(location && location.search ? location.search : '');
    const pending = readPendingInvite();
    const invite = inviteFromUrl || pending;
    const inviteKey = String(invite || '').trim();

    if (!inviteKey) return;
    if (joinBusy) return;

    if (!isSignedIn && meLoading) return;

    if (!isSignedIn) {
      writePendingInvite(inviteKey);
      if (!inviteLoginModalOpen && inviteLoginDismissedRef.current !== inviteKey) openInviteLoginModal(inviteKey);
      return;
    }

    if (inviteLoginModalOpen) {
      setInviteLoginModalOpen(false);
    }

    if (inviteModalOpen) return;
    if (autoInvitePromptedRef.current === inviteKey) return;

    autoInvitePromptedRef.current = inviteKey;
    openInviteJoinModal(inviteKey, { defaultPublicName: pickDisplayName(user) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isSignedIn, meLoading, joinBusy, inviteModalOpen, inviteLoginModalOpen, user]);

  function handleEnterInviteCode() {
    setInviteEntryValue('');
    setInviteEntryOpen(true);
  }

  function submitInviteEntry() {
    const code = normaliseInviteInput(inviteEntryValue);
    if (!code) return;
    setInviteEntryOpen(false);
    inviteLoginDismissedRef.current = '';
    if (!isSignedIn) {
      writePendingInvite(code);
      openInviteLoginModal(code);
      return;
    }
    if (joinBusy) return;
    openInviteJoinModal(code, { defaultPublicName: pickDisplayName(user) });
  }

  async function handleCreateServer() {
    if (!isSignedIn) {
      triggerOAuthLogin();
      return;
    }

    const raw = window.prompt('Server name:', 'My server');
    const name = String(raw || '').trim();
    if (!name) return;

    try {
      const res = await fetch('/api/coms/servers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });

      const data = await res.json().catch(() => null);

      if (res.status === 401) {
        triggerOAuthLogin();
        return;
      }

      if (!res.ok || !data || data.error) {
        setActionNote((data && data.error) || 'Failed to create server.');
        return;
      }

      const sid = pickServerIdFromPayload(data);
      navigate(sid ? `/comms/${encodeURIComponent(sid)}` : '/comms');
    } catch (e) {
      setActionNote('Failed to create server.');
    }
  }

  const handleLaunch = () => {
    if (!isSignedIn) {
      triggerOAuthLogin();
      return;
    }
    navigate(buildComsLaunchPath());
  };

  const handleOpenCanvas = () => {
    navigate('/canvas');
  };

  // Areas of the Voxemera shell (Studio, Watch Together, Direct Messages) open inside a server.
  function openArea(area, extra) {
    if (!isSignedIn) {
      triggerOAuthLogin();
      return;
    }
    if (myServers.status === 'ok' && !myServers.servers.length) {
      setActionNote(`${AREA_LABELS[area] || 'This area'} opens inside a server. Join a public server, use an invite code, or create your own first.`);
      return;
    }
    navigate(comsPathForArea(homeServerKey, area, extra));
  }

  function openServer(server) {
    const key = typeof server === 'string' ? server : serverKeyOf(server);
    if (!key) return;
    navigate(comsPathForArea(key, 'coms'));
  }

  useEffect(() => {
    if (!accountOpen) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      if (e.type === 'mousedown' && e.target.closest && e.target.closest('.vxw-account')) return;
      setAccountOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [accountOpen]);

  function scrollToPublic() {
    const el = publicSectionRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    } catch {}
  }

  function onActionCard(id) {
    setActionNote('');
    if (id === 'canvas') handleOpenCanvas();
    else if (id === 'studio') openArea('studio');
    else if (id === 'browse') scrollToPublic();
    else if (id === 'invite') handleEnterInviteCode();
  }

  // ---------- joining a listed server ----------
  const joinPublicServer = useCallback(
    async (server) => {
      const key = server && server.key;
      if (!key) return;
      setPublicBusyKey(key);
      setPublicNotes((n) => ({ ...n, [key]: null }));
      try {
        const data = await joinWithConsent(`/api/coms/discover/servers/${encodeURIComponent(key)}/join`, {});
        writePendingPublicJoin('');
        setPendingPublicJoin('');
        if (data && data.pending) {
          setPublicNotes((n) => ({ ...n, [key]: { type: 'ok', text: 'Request sent. A moderator will let you in soon.' } }));
          loadPublicServers();
          return;
        }
        navigate(comsPathForArea(key, 'coms'));
      } catch (e) {
        writePendingPublicJoin('');
        setPendingPublicJoin('');
        if (e && e.status === 401) {
          setPublicLoginServer(server);
          return;
        }
        if (e && e.status === 404) loadPublicServers();
        setPublicNotes((n) => ({ ...n, [key]: { type: 'error', text: (e && e.message) || 'Could not join this server.' } }));
      } finally {
        setPublicBusyKey('');
      }
    },
    [navigate, loadPublicServers]
  );

  function handleJoinPublic(server) {
    if (!isSignedIn) {
      setPublicLoginServer(server);
      return;
    }
    joinPublicServer(server);
  }

  // A join pressed before signing in finishes once the account is known.
  useEffect(() => {
    if (!pendingPublicJoin || !isSignedIn || meLoading) return;
    if (pendingJoinRanRef.current === pendingPublicJoin) return;
    pendingJoinRanRef.current = pendingPublicJoin;
    setStayOnLanding(true);
    joinPublicServer({ key: pendingPublicJoin });
  }, [pendingPublicJoin, isSignedIn, meLoading, joinPublicServer]);

  useEffect(() => {
    if (isSignedIn && publicLoginServer) setPublicLoginServer(null);
  }, [isSignedIn, publicLoginServer]);

  // ---------- installing ----------
  function startDownload(url, file, external) {
    try {
      const a = document.createElement('a');
      a.href = url;
      if (external) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      } else {
        a.setAttribute('download', file || '');
        a.rel = 'noopener';
      }
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    } catch {}
    try {
      window.location.href = url;
    } catch {}
  }

  async function promptBrowserInstall(kind) {
    const promptEvent = deferredInstallPrompt;
    if (!promptEvent) {
      setInstallHelp(kind);
      return;
    }
    setInstallBusy(kind === 'android' ? 'pwa-android' : 'pwa');
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice.catch(() => null);
      if (choice && choice.outcome === 'accepted') writeNudgeState(markActed(readNudgeState()));
    } catch {
      setInstallHelp(kind);
    } finally {
      setDeferredInstallPrompt(null);
      setCanInstall(false);
      setInstallBusy('');
    }
  }

  // option: from appPromotion(). followed = a real link already started the download / opened the store page.
  async function handlePromoSelect(option, { followed = false } = {}) {
    if (!option) return;
    setNudgeVisible(false);
    if (option.kind === 'native') {
      writeNudgeState(markActed(readNudgeState()));
      if (followed) return;
      // The Windows button can be pressed before the build list arrives: resolve it now.
      setInstallBusy(option.id);
      const av = availabilityRef.current || (await loadAvailability());
      setInstallBusy('');
      const build = av && av[option.id];
      if (build && build.url) startDownload(build.url, build.file, build.store);
      else setInstallHelp('unavailable');
      return;
    }
    if (option.id === 'pwa') promptBrowserInstall('pwa');
    else if (option.id === 'pwa-android') promptBrowserInstall('android');
    else if (option.id === 'ios-home') setInstallHelp('ios');
    else setInstallHelp(option.id); // mac-dock, android-elsewhere, ios-elsewhere
  }

  if (shouldHoldLandingPaint) {
    // Signed-in visitors are sent on to the app before anything paints; while the session is being checked a
    // quiet branded loader appears only if the check is slow.
    if (shouldBootRedirect) return null;
    return (
      <div className="vxw-boot" role="status" aria-live="polite">
        <img src={logoSrc} alt="" width="56" height="56" />
        <span className="vxw-boot-text">Loading Voxemera…</span>
      </div>
    );
  }

  const noServersYet = isSignedIn && myServers.status === 'ok' && !myServers.servers.length;
  const siteHost = typeof window !== 'undefined' ? window.location.host : 'voxemera.com';

  return (
    <div className={`vxw ${reduceMotion ? 'vxw-reduce' : ''}`}>
      <header className="vxw-header">
        <div className="vxw-header-inner">
          <div className="vxw-brand">
            <img src={logoSrc} alt="" width="50" height="50" />
            <span className="vxw-brand-text">
              <span className="vxw-brand-name">Voxemera</span>
              <span className="vxw-brand-by">by Point Atlas</span>
            </span>
          </div>

          <nav className="vxw-nav" aria-label="Welcome">
            <button type="button" className="vxw-nav-item is-active" aria-current="page" aria-label="Home" title="Home" onClick={() => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })}>
              {Icon.home(20)}
              <span>Home</span>
            </button>
            <div className="vxw-nav-anchor">
              <button
                type="button"
                className="vxw-nav-item"
                title="Communities"
                aria-label="Communities"
                data-vxw-communities
                aria-haspopup={isSignedIn ? 'menu' : undefined}
                aria-expanded={isSignedIn ? communitiesOpen : undefined}
                onClick={() => (isSignedIn ? setCommunitiesOpen((v) => !v) : triggerOAuthLogin())}
              >
                {Icon.people(21)}
                <span>Communities</span>
              </button>
              <CommunitiesMenu
                open={communitiesOpen}
                onClose={() => setCommunitiesOpen(false)}
                servers={myServers.servers}
                logos={logos}
                loaded={myServers.status === 'ok'}
                onOpenServer={(s) => {
                  setCommunitiesOpen(false);
                  openServer(s);
                }}
                onCreateServer={() => {
                  setCommunitiesOpen(false);
                  handleCreateServer();
                }}
              />
            </div>
            <button type="button" className="vxw-nav-item" onClick={scrollToPublic} aria-label="Discover public servers" title="Discover">
              {Icon.compass(20)}
              <span>Discover</span>
            </button>
          </nav>

          <div className="vxw-header-right">
            <AppGet promo={promo} busy={installBusy} onSelect={handlePromoSelect} />
            {isSignedIn ? (
              <div className="vxw-account">
                <button type="button" className="vxw-account-btn" onClick={() => setAccountOpen((v) => !v)} aria-haspopup="menu" aria-expanded={accountOpen} title={displayName ? `Signed in as ${displayName}` : 'Signed in'}>
                  <span className="vxw-account-dot">{initialsOf(displayName || 'You')}</span>
                </button>
                {accountOpen ? (
                  <div className="vxw-menu vxw-menu-right" role="menu">
                    <div className="vxw-menu-title">Signed in as {displayName || 'you'}</div>
                    <button type="button" role="menuitem" className="vxw-menu-item" onClick={handleLaunch}>
                      <span className="vxw-menu-name">Open Voxemera</span>
                      {Icon.arrow(16)}
                    </button>
                    {typeof onLogout === 'function' ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="vxw-menu-item"
                        onClick={() => {
                          setAccountOpen(false);
                          onLogout();
                        }}
                      >
                        <span className="vxw-menu-name">Log out</span>
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div ref={loginBtnWrapRef} className="vxw-login-wrap">
                <LoginButton className="vxw-login">Log in</LoginButton>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="vxw-main" id="vxw-main">
        <section className="vxw-hero" aria-labelledby="vxw-title">
          <h1 id="vxw-title" className="vxw-title">
            Voxemera
          </h1>
          <p className="vxw-lead">
            Communities, creative tools and shared media, with <strong className="vxw-lead-accent">proximity voice on maps</strong>.
          </p>

          <div className="vxw-actions">
            {ACTION_CARDS.map((a) => (
              <ActionButton key={a.id} icon={a.icon} title={a.title} sub={a.id === 'studio' && noServersYet ? 'Opens inside a server' : a.sub} onClick={() => onActionCard(a.id)} disabled={a.id === 'invite' && joinBusy} />
            ))}
          </div>

          {actionNote ? (
            <p className="vxw-note vxw-action-note" role="status">
              {actionNote}
            </p>
          ) : null}
        </section>

        <PublicServers
          state={publicState}
          expanded={showAllServers}
          onToggleExpanded={() => setShowAllServers((v) => !v)}
          onRetry={loadPublicServers}
          onJoin={handleJoinPublic}
          onOpen={openServer}
          busyKey={publicBusyKey}
          notes={publicNotes}
          isSignedIn={isSignedIn}
          onCreateServer={handleCreateServer}
          onEnterInvite={handleEnterInviteCode}
          sectionRef={publicSectionRef}
        />

        <ContinuePanel
          isSignedIn={isSignedIn}
          recent={recent}
          myServers={myServers}
          onArea={openArea}
          onOpenServer={openServer}
          onOpenDm={(d) => openArea('social', { socialSection: 'dms', threadId: d.id })}
          onResume={(r) => navigate(`/comms/${encodeURIComponent(r.serverKey)}`)}
          onSignIn={triggerOAuthLogin}
          onCreateServer={handleCreateServer}
          onBrowse={scrollToPublic}
          onClearRecent={clearRecent}
        />
        <footer className="vxw-footer">
          <span>© {new Date().getFullYear()} Voxemera</span>
          <span className="vxw-footer-sep" aria-hidden="true">
            |
          </span>
          <span>Independent voice, video, servers, canvas and maps. No data sharing, no AI profiling.</span>
          <span className="vxw-footer-sep" aria-hidden="true">
            |
          </span>
          <a href="/privacy">Privacy</a>
        </footer>
      </main>

      {isSignedIn ? <EncryptionConsentHost /> : null}

      {inviteEntryOpen ? (
        <ComsModal
          title="Enter invite code"
          onClose={() => setInviteEntryOpen(false)}
          footer={
            <>
              <button type="button" className="coms-mini-btn" onClick={() => setInviteEntryOpen(false)}>
                Cancel
              </button>
              <button type="button" className="coms-mini-btn primary" onClick={submitInviteEntry} disabled={!inviteEntryValue.trim()}>
                Continue
              </button>
            </>
          }
        >
          <form
            className="coms-form"
            onSubmit={(e) => {
              e.preventDefault();
              submitInviteEntry();
            }}
          >
            <label className="coms-form-row">
              <div className="coms-form-label">Invite code or link</div>
              <input value={inviteEntryValue} onChange={(e) => setInviteEntryValue(e.target.value)} placeholder="SERVERKEY:INVITECODE" autoFocus autoComplete="off" spellCheck={false} />
              <div className="coms-form-hint">Paste the code a server member shared with you, or the whole invite link.</div>
            </label>
          </form>
        </ComsModal>
      ) : null}

      {publicLoginServer ? (
        <ComsModal
          title={`Join ${publicLoginServer.name || 'this server'}`}
          onClose={() => setPublicLoginServer(null)}
          footer={
            <>
              <button type="button" className="coms-mini-btn" onClick={() => setPublicLoginServer(null)}>
                Cancel
              </button>
              <LoginButton
                className="coms-mini-btn primary"
                redirectPath="/"
                onClick={() => {
                  writePendingPublicJoin(publicLoginServer.key);
                  setPendingPublicJoin(publicLoginServer.key);
                }}
              >
                Log in to join
              </LoginButton>
            </>
          }
        >
          <div className="coms-form">
            <div className="coms-form-hint">Log in to Voxemera and you’ll join {publicLoginServer.name || 'the server'} straight after.</div>
          </div>
        </ComsModal>
      ) : null}

      {installHelp ? (
        <ComsModal
          title={
            installHelp === 'ios' || installHelp === 'ios-elsewhere'
              ? 'Add Voxemera to your Home Screen'
              : installHelp === 'android' || installHelp === 'android-elsewhere'
                ? 'Install Voxemera on Android'
                : installHelp === 'mac-dock'
                  ? 'Add Voxemera to your Dock'
                  : installHelp === 'unavailable'
                    ? 'Download not available right now'
                    : 'Install Voxemera'
          }
          onClose={() => setInstallHelp('')}
          footer={
            <button type="button" className="coms-mini-btn primary" onClick={() => setInstallHelp('')}>
              Got it
            </button>
          }
        >
          <div className="coms-form">
            {installHelp === 'ios' || installHelp === 'ios-elsewhere' ? (
              <ol className="vxw-steps">
                <li>{installHelp === 'ios-elsewhere' ? 'On your iPhone or iPad, open' : 'Open'} {siteHost} in Safari.</li>
                <li>Tap the Share button.</li>
                <li>Choose “Add to Home Screen”, then “Add”.</li>
              </ol>
            ) : installHelp === 'android' || installHelp === 'android-elsewhere' ? (
              <ol className="vxw-steps">
                <li>{installHelp === 'android-elsewhere' ? 'On your Android phone, open' : 'Open'} {siteHost} in Chrome.</li>
                <li>Tap the ⋮ menu in the top-right corner.</li>
                <li>Choose “Install app” (or “Add to Home screen”), then confirm.</li>
              </ol>
            ) : installHelp === 'mac-dock' ? (
              <ol className="vxw-steps">
                <li>In Safari, open the File menu (or the Share button).</li>
                <li>Choose “Add to Dock”, then “Add”.</li>
              </ol>
            ) : installHelp === 'unavailable' ? (
              <div className="coms-form-hint">The download list couldn’t be reached just now. Please try again in a moment; Voxemera keeps working here in your browser in the meantime.</div>
            ) : (
              <ol className="vxw-steps">
                <li>Look for the install icon at the right of the address bar, or open the browser menu.</li>
                <li>Choose “Install Voxemera” (or “Apps → Install this site as an app”).</li>
              </ol>
            )}
            {installHelp === 'unavailable' ? null : (
              <div className="coms-form-hint">
                {installHelp === 'android-elsewhere' || installHelp === 'ios-elsewhere' || installHelp === 'mac-dock' || installHelp === 'ios'
                  ? 'This installs Voxemera’s web app: it gets its own icon and window, with no app store needed, and you can keep using the browser any time.'
                  : 'Your browser hasn’t offered its one-tap install on this visit, so here is how to do it from the browser menu. This installs Voxemera’s web app.'}
              </div>
            )}
          </div>
        </ComsModal>
      ) : null}

      {nudgeVisible && !installHelp ? <AppNudge option={nudgeOption} onSelect={handlePromoSelect} onDismiss={dismissAppNudge} /> : null}

      {inviteLoginModalOpen ? (
        <ComsModal
          title="Login required"
          onClose={cancelInviteLoginModal}
          footer={
            <>
              <button type="button" className="coms-mini-btn" onClick={cancelInviteLoginModal}>
                Cancel
              </button>
              <button
                type="button"
                className="coms-mini-btn primary"
                onClick={() => {
                  closeInviteLoginModal();
                  triggerOAuthLogin();
                }}
              >
                OK
              </button>
            </>
          }
        >
          <div className="coms-form">
            <div className="coms-form-row">
              <div className="coms-form-label">Invite detected</div>
              <input value={inviteLoginModalCode} readOnly />
              <div className="coms-form-hint">
                Log in to accept this invite. After login, you’ll be shown the public name other users may see.
              </div>
            </div>
          </div>
        </ComsModal>
      ) : null}

      {inviteModalOpen ? (
        <ComsModal
          title="Join server"
          onClose={closeInviteJoinModal}
          footer={
            <>
              <button type="button" className="coms-mini-btn" onClick={closeInviteJoinModal} disabled={joinBusy}>
                Cancel
              </button>
              <button type="button" className="coms-mini-btn" onClick={() => submitInviteJoinFromModal(false)} disabled={joinBusy}>
                {joinBusy ? 'Joining…' : 'Keep current name and join'}
              </button>
              <button
                type="button"
                className="coms-mini-btn primary"
                onClick={() => submitInviteJoinFromModal(true)}
                disabled={joinBusy}
              >
                {joinBusy ? 'Joining…' : 'Update name and join'}
              </button>
            </>
          }
        >
          <div className="coms-form">
            <div className="coms-form-row">
              <div className="coms-form-label">Invite code</div>
              <input value={inviteModalCode} readOnly />
            </div>

            <div className="coms-form-row">
              <div className="coms-form-label">Your current public name</div>
              <input value={displayName || ''} readOnly />
              <div className="coms-form-hint">
                This may come from your sign-in provider. Other users may see this name in social features and direct messages.
              </div>
            </div>

            <label className="coms-form-row">
              <div className="coms-form-label">Choose a different public name (optional)</div>
              <input
                value={inviteModalPublicName}
                onChange={(e) => setInviteModalPublicName(e.target.value)}
                placeholder="Enter the public name you want to use"
                autoFocus
                disabled={joinBusy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitInviteJoinFromModal(true);
                  }
                }}
              />
              <div className="coms-form-hint">
                This updates your account username before joining. Leave it as-is if you are happy with your current public name.
              </div>
            </label>

            {inviteModalErr ? <div className="coms-form-error">{inviteModalErr}</div> : null}
          </div>
        </ComsModal>
      ) : null}
    </div>
  );
}
